// Note: do not assign a string enum value. We use the implicit number value in the debugger.
export enum FaceCategory {
  CASEWORK,
  CEILING,
  DOOR_DECORATION,
  DOOR_EXTERIOR,
  DOOR_CURVED_WALL_EXTERIOR,
  DOOR_INTERIOR,
  DOOR_CURVED_WALL_INTERIOR,
  CORNER_BOARD,
  FACADE_WALL_EXTERIOR,
  FLOOR,
  FLOOR_FRAMING,
  SUB_FLOORING,
  SLAB_FLOORING,
  FOUNDATION_FOOTING,
  FOUNDATION_GARAGE_CURB,
  FOUNDATION_SLAB,
  FOUNDATION_STEM_WALL,
  FOUNDATION_PIER,
  GROUND,
  PORCH_DECORATION,
  PORCH_EXTERIOR,
  PROP_APPLIANCES,
  PROP_FIXTURES,
  PROP_FURNITURE,
  PROP_OTHER,
  PROP_FOLIAGE,
  RAILING,
  STAIR,
  ROOF_BOTTOM,
  ROOF_DORMER_WALL,
  ROOF_RIDGE_VENT,
  ROOF_RIDGE_CAP,
  ROOF_TOP,
  ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY, // This is roof geometry that can be tweaked for export purposes
  ROOF_FRIEZE_BOARD,
  ROOF_FASCIA_BOARD,
  ROOF_SOFFIT,
  ROOF_FASCIA_TOP_TRIM,
  ROOF_BELLY_BAND_BOARD,
  ROOF_WALL_BASE_BOARD,
  ROOF_EXPOSED_RAFTER,
  ROOF_GABLE_VENT,
  ROOF_WALL_EXTERIOR,
  ROOF_WALL_TRIM,
  ROOF_FRAMING,
  ROOM_WALL_BREP,
  ROOM_WALL_EXTERIOR,
  ROOM_WALL_EXTRUSION,
  ROOM_WALL_INTERIOR,
  WAINSCOTING,
  WAINSCOTING_TRIM,
  WALL_FRAMING,
  GABLE_END_WALL_FRAMING,
  WINDOW_DECORATION,
  WINDOW_EXTERIOR,
  WINDOW_GRID,
  WINDOW_GLASS,
  WINDOW_INTERIOR,
  WINDOW_SCREEN,
  BASE_TRIM_MOLDING,
  CROWN_TRIM_MOLDING,
  COLUMN,
  SKYLIGHT_GLASS,
  SKYLIGHT_EXTERIOR,
  SKYLIGHT_DECORATION,
  // Site geometry categories
  SITE_ROAD,
  SITE_GRASS,
  SITE_SIDEWALK,
  SITE_CURB,
  SITE_DRIVEWAY,
  SITE_FENCE,
  SITE_TOPOGRAPHY,
  SITE_WHITEBOX,
  SITE_PLANE,
}

export const roofTrimFaceCategories: readonly FaceCategory[] = [
  FaceCategory.ROOF_FRIEZE_BOARD,
  FaceCategory.ROOF_FASCIA_BOARD,
  FaceCategory.ROOF_SOFFIT,
  FaceCategory.ROOF_FASCIA_TOP_TRIM,
  FaceCategory.ROOF_BELLY_BAND_BOARD,
  FaceCategory.ROOF_WALL_BASE_BOARD,
  FaceCategory.ROOF_EXPOSED_RAFTER,
  FaceCategory.ROOF_GABLE_VENT,
];

export const allFaceCategories: readonly FaceCategory[] = Object.values(FaceCategory)
  .map((item) => Number(item))
  .filter((item) => !isNaN(item));

export const defaultIterateFaceCategories: Set<FaceCategory> = new Set(
  [...allFaceCategories].filter(
    (v) =>
      v !== FaceCategory.PROP_FOLIAGE &&
      v !== FaceCategory.PROP_FURNITURE &&
      v !== FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY &&
      v !== FaceCategory.ROOF_RIDGE_CAP &&
      v !== FaceCategory.ROOF_RIDGE_VENT &&
      v !== FaceCategory.WALL_FRAMING &&
      v !== FaceCategory.GABLE_END_WALL_FRAMING &&
      v !== FaceCategory.FLOOR,
  ),
);

export const defaultFaceCategoriesNoGround: Set<FaceCategory> = new Set(
  [...defaultIterateFaceCategories].filter((v) => v !== FaceCategory.GROUND),
);

export const foundationFaceCategories: readonly FaceCategory[] = [
  FaceCategory.FOUNDATION_FOOTING,
  FaceCategory.FOUNDATION_GARAGE_CURB,
  FaceCategory.FOUNDATION_SLAB,
  FaceCategory.FOUNDATION_STEM_WALL,
  FaceCategory.FOUNDATION_PIER,
];

export const propFaceCategories: readonly FaceCategory[] = [
  FaceCategory.PROP_APPLIANCES,
  FaceCategory.PROP_FIXTURES,
  FaceCategory.PROP_FURNITURE,
  FaceCategory.PROP_OTHER,
  FaceCategory.PROP_FOLIAGE,
];

export const siteFaceCategories: readonly FaceCategory[] = [
  FaceCategory.SITE_ROAD,
  FaceCategory.SITE_GRASS,
  FaceCategory.SITE_SIDEWALK,
  FaceCategory.SITE_CURB,
  FaceCategory.SITE_DRIVEWAY,
  FaceCategory.SITE_FENCE,
  FaceCategory.SITE_TOPOGRAPHY,
  FaceCategory.SITE_WHITEBOX,
  FaceCategory.SITE_PLANE,
];

export const doorFaceCategories: readonly FaceCategory[] = [
  FaceCategory.DOOR_EXTERIOR,
  FaceCategory.DOOR_INTERIOR,
  FaceCategory.DOOR_CURVED_WALL_EXTERIOR,
  FaceCategory.DOOR_CURVED_WALL_INTERIOR,
  // NOTE: Does not include DOOR_DECORATION.
];

export const foundationOcclusionFaceCategories: readonly FaceCategory[] = [
  FaceCategory.ROOM_WALL_EXTERIOR,
  // TODO: Does this need other wall faceCategories?
  FaceCategory.ROOF_TOP,
  FaceCategory.WINDOW_EXTERIOR,
  FaceCategory.DOOR_EXTERIOR,
  FaceCategory.FLOOR,
  FaceCategory.CEILING,
  FaceCategory.CORNER_BOARD,
];

export const wallFaceCategories: readonly FaceCategory[] = [
  FaceCategory.ROOM_WALL_EXTERIOR,
  FaceCategory.ROOM_WALL_INTERIOR,
  FaceCategory.ROOM_WALL_BREP,
  FaceCategory.ROOM_WALL_EXTRUSION,
  FaceCategory.FACADE_WALL_EXTERIOR,
  FaceCategory.ROOF_WALL_EXTERIOR,
  FaceCategory.ROOF_WALL_TRIM,
  FaceCategory.ROOF_DORMER_WALL,
];

export function getRoofPlanFaceCategories(includeRidgeVent = false): Set<FaceCategory> {
  const faceCategories = new Set<FaceCategory>([
    FaceCategory.ROOF_TOP,
    FaceCategory.ROOF_BOTTOM, // NOTE: This is included to minimize baseline diffs. Could potentially be removed?
    FaceCategory.SKYLIGHT_EXTERIOR,
    FaceCategory.SKYLIGHT_DECORATION,
  ]);

  if (includeRidgeVent) {
    faceCategories.add(FaceCategory.ROOF_RIDGE_VENT);
  }
  return faceCategories;
}

export type FaceGroupName =
  | "Casework"
  | "Ceilings"
  | "Door"
  | "Doors"
  | "Exterior Trim"
  | "Floor Framing"
  | "Slab Flooring"
  | "Sub Flooring"
  | "Floors"
  | "Foundation"
  | "Porch"
  | "Porches"
  | "Railings"
  | "Roof Ridge Vents"
  | "Roof Trim"
  | "Roof Ridge Caps"
  | "Roof"
  | "Roofs"
  | "Site"
  | "Skylight"
  | "Wainscoting"
  | "Walls"
  | "Wall Framing"
  | "Gable End Wall Framing"
  | "Window"
  | "Window Shutters"
  | "Windows"
  | "Windows Shutters"
  | "Base Trim Molding"
  | "Crown Trim Molding"
  | "Column"
  | "Mesh Bounding Boxes"
  | "Analytic";
